/**
 * The cart: what the character carries, how it reaches him, and what it does.
 *
 * ## The trap this file exists to avoid
 *
 * `world.mods` is a flat bag of multipliers, and relics, tech nodes, Chronicle
 * answers and hero passives all *fold* into it permanently — `unitDamage *=
 * 1.25` and it is 1.25 forever. Items are not permanent. They come off again.
 *
 * The obvious implementation is to multiply on equip and divide on unequip.
 * That is wrong, and it is wrong quietly. `(x * 1.04) / 1.04` is not `x` in
 * binary floating point; it is `x` plus or minus an ulp, and a player who swaps
 * a weapon forty times over a long run has accumulated forty ulps of drift in a
 * number that decides how much damage every soldier deals. Two worlds that
 * received the same commands in a different *order* would then hash differently,
 * which is a replay desync with no visible cause and no way to bisect it.
 *
 * So nothing is ever divided out. `world.modsBase` holds everything permanent;
 * `world.mods` is **rebuilt from it** by `refoldEquipment` every time the kit
 * changes. Unequipping is not the inverse of equipping, it is a fresh fold of a
 * smaller set. The same items in the same slots therefore always produce
 * bit-identical numbers, because it is always the same short sequence of
 * multiplications applied to the same base, walked in slot order.
 *
 * The permanent half is held to the same discipline from the other side: see
 * `foldPermanent` in `src/sim/apply.ts`, which is the only door a relic, a tech
 * node, an event or a hero passive gets to write through.
 *
 * ## Army-wide versus personal
 *
 * Every item's `mods` go through the bag, so the whole army feels it — that is
 * the point of the system, and it is why a Seal that speeds the sawmills is a
 * legitimate thing to hang on a warlord. On top of that, Weapons and Armour
 * carry a *personal* effect that cannot live in an army-wide bag: `heroDamage`
 * is read live by `heroDamageMul` in `hero.ts`, and `heroWard` is read live by
 * `damageUnit` in `combat.ts`. Both are pure functions of the equipped set —
 * they store nothing, so there is nothing to unfold and nothing to drift.
 */

import { LogTone, pushLog } from '../events.ts';
import { canAfford, spend, Team, type ResourceBundle } from '../types.ts';
import {
  defaultMods,
  type ItemStack,
  type Mods,
  type World,
} from '../world.ts';
import { nextInt, pickWeighted } from '../../core/rng.ts';
import { buildingType } from '../../data/buildings.ts';
import {
  ITEMS,
  ITEM_RARITY_NAMES,
  ITEM_SLOT_COUNT,
  ITEM_SLOT_NAMES,
  itemDef,
  itemsAvailable,
  type ItemDef,
} from '../../data/items.ts';

// ── Tuning ─────────────────────────────────────────────────────────────────

/**
 * How much the cart holds.
 *
 * A cap rather than an infinity, and not for memory: an inventory with no
 * ceiling is an inventory nobody ever looks at, and every find after the tenth
 * is a scroll bar. Twenty-four leaves room to hold two or three candidates per
 * slot and forces the rest through the scrap heap, which is where the souls to
 * reforge the keepers come from. The loop closes on itself.
 */
export const INVENTORY_MAX = 24;

/** Chance that surviving a Siege turns something up. */
export const HOARD_CHANCE = 0.5;

/** A dropped item's level climbs with the wave, one step per this many waves. */
export const HOARD_LEVEL_PER_WAVE = 6;

/** Base drop weight per rarity, indexed 0..4 (0 unused). */
const RARITY_WEIGHT = [0, 10, 5, 2, 1];

/**
 * The floor under any "lower is better" multiplier.
 *
 * `buildCost: -0.03` per level would reach zero at level 33 and go negative at
 * 34, which is a building that pays you to raise it. Levels have no ceiling by
 * design, so the clamp has to be here rather than in a maximum level.
 */
const MIN_MULTIPLIER = 0.05;

/**
 * Keys the bag combines by taking the better of the two rather than stacking.
 *
 * The same three `hero.ts` names, and deliberately a second copy rather than an
 * import: that file's list is private to its own fold and describes the same
 * rule for a different writer. If a fourth key ever joins, both lists want it,
 * and a test that equips a `moraleFloor` item twice would catch the miss.
 */
const MOD_BEST_OF = new Set(['moraleFloor', 'corpseRise', 'secondWind']);

// ── Scaling ────────────────────────────────────────────────────────────────

/**
 * An item's catalogue deltas, resolved into actual modifier values at a level.
 *
 * `defaultMods()` decides the reading: a key that starts at 1 is a multiplier
 * and the delta is a fraction of it; a key that starts at 0 is additive and the
 * delta is the amount. That is the same authority `hero.ts` consults, which is
 * why `src/data/items.ts` is allowed to be ignorant of the distinction.
 *
 * `willMax` is refused outright. It is the one key with a second home —
 * `world.willMax` is a separate field that relics bump alongside the bag — and
 * an item that grants Will could not take it back off without a ledger of who
 * granted what. No item in the catalogue asks for it; this is the guard that
 * keeps it that way.
 */
export function itemModValues(def: ItemDef, level: number): Record<string, number> {
  const base = defaultMods() as unknown as Record<string, number>;
  const out: Record<string, number> = {};
  const lvl = Math.max(1, Math.floor(level));
  for (const k of Object.keys(def.mods)) {
    if (k === 'willMax' || !(k in base)) continue;
    const delta = def.mods[k];
    if (delta === undefined || delta === 0) continue;
    out[k] = base[k] === 0 ? delta * lvl : Math.max(MIN_MULTIPLIER, 1 + delta * lvl);
  }
  return out;
}

/** Fold one resolved set of values into a bag, by the bag's own rules. */
function foldInto(bag: Record<string, number>, values: Record<string, number>): void {
  const base = defaultMods() as unknown as Record<string, number>;
  for (const k of Object.keys(values)) {
    const v = values[k];
    if (!(k in bag)) continue;
    if (MOD_BEST_OF.has(k)) bag[k] = Math.max(bag[k], v);
    else if (base[k] === 0) bag[k] += v;
    else bag[k] *= v;
  }
}

/**
 * Rebuild `world.mods` from `world.modsBase` plus whatever is equipped.
 *
 * Call this after *any* change to the kit or to the base. It is idempotent and
 * cheap — five slots and a handful of keys — so calling it once too often costs
 * nothing, and calling it once too few is the bug.
 *
 * The bag object is mutated in place rather than replaced, because the UI and
 * the balance harness both hold `w.mods` across frames.
 */
export function refoldEquipment(w: World): void {
  const bag = w.mods as unknown as Record<string, number>;
  const base = w.modsBase as unknown as Record<string, number>;
  for (const k of Object.keys(base)) bag[k] = base[k];
  for (let slot = 0; slot < ITEM_SLOT_COUNT; slot++) {
    const st = equippedStack(w, slot);
    if (!st) continue;
    const def = itemDef(st.defId);
    if (!def) continue;
    foldInto(bag, itemModValues(def, st.level));
  }
}

/**
 * The only door a *permanent* modifier gets to write through.
 *
 * The other half of `refoldEquipment`'s contract, and it lives beside it for
 * that reason. `w.mods` is what every system reads, and since items exist it is
 * **derived**: `w.modsBase` plus whatever the character is carrying. Relics,
 * tech nodes, Chronicle answers and hero passives are not carried — they fold in
 * and stay — so they belong in the base, and every one of them writes
 * `w.mods.x *= n` directly, across fifty relics, forty tech nodes and
 * forty-seven event cards. Rewriting all of those to name a different field
 * would be a hundred and forty chances to miss one, and a missed one is a
 * purchase that silently evaporates the next time the player changes a sword.
 *
 * So the field is pointed at the base for the duration of the write, the result
 * is kept as the new base, and the equipment is folded back on top. The writers
 * are untouched and cannot get it wrong; there is exactly one place that has to
 * be right, and this is it.
 *
 * The one honest wart: while `write` runs, a *read* of `w.mods` sees the bag
 * without equipment. That matters for the handful of Chronicle cards that spawn
 * units inside their own `apply` — those men are raised to the army's permanent
 * health rather than to the health the hero's kit would have given them. It is
 * deterministic, it is one tick of one card, and the alternative is dividing
 * multipliers back out, which is the bug this whole design exists to avoid.
 */
export function foldPermanent<T>(w: World, write: () => T): T {
  const effective = w.mods;
  w.mods = w.modsBase;
  const out = write();
  w.modsBase = w.mods;
  w.mods = effective;
  refoldEquipment(w);
  return out;
}

// ── Queries ────────────────────────────────────────────────────────────────

export function itemByUid(w: World, uid: number): ItemStack | undefined {
  return w.inventory.find((s) => s.uid === uid);
}

/** What is in a slot, or undefined. */
export function equippedStack(w: World, slot: number): ItemStack | undefined {
  const uid = w.equipped[slot] ?? -1;
  if (uid < 0) return undefined;
  return itemByUid(w, uid);
}

export function isEquipped(w: World, uid: number): boolean {
  return w.equipped.includes(uid);
}

/** The slot a uid currently sits in, or −1. */
export function slotOf(w: World, uid: number): number {
  return w.equipped.indexOf(uid);
}

/**
 * The hero's own damage, from what he is holding.
 *
 * A pure function of the equipped set, read on every swing by `heroDamageMul`.
 * Nothing is stored, so nothing can drift: take the sword off and the number is
 * 1 again by arithmetic rather than by bookkeeping.
 */
export function heroGearDamageMul(w: World): number {
  let mul = 1;
  for (let slot = 0; slot < ITEM_SLOT_COUNT; slot++) {
    const st = equippedStack(w, slot);
    if (!st) continue;
    const d = itemDef(st.defId);
    if (d?.heroDamage) mul *= 1 + d.heroDamage * st.level;
  }
  return mul;
}

/**
 * What the hero's own body shrugs off, as a multiplier on damage taken.
 *
 * Diminishing rather than subtractive — `1 / (1 + ward)` — so a hundred levels
 * of harness approaches invulnerability without ever reaching it, and no amount
 * of stacking can produce a negative hit that heals him.
 */
export function heroWardMul(w: World): number {
  let ward = 0;
  for (let slot = 0; slot < ITEM_SLOT_COUNT; slot++) {
    const st = equippedStack(w, slot);
    if (!st) continue;
    const d = itemDef(st.defId);
    if (d?.heroWard) ward += d.heroWard * st.level;
  }
  return ward > 0 ? 1 / (1 + ward) : 1;
}

/**
 * A fire big enough to work iron, or a room with the right tools in it.
 *
 * Finding needs nothing; *improving* needs a building, which is what ties the
 * character's kit back into the city the game is otherwise about. All three of
 * these are second-Age buildings, and that gate is deliberate rather than
 * incidental: in the Ember Age you wear what you find, and the day you can start
 * feeding it is the day you commit to the Kindling. It reads the same way the
 * ingot line in `reforgeCost` does.
 */
const FORGES = new Set(['smelter', 'smeltery', 'armoury']);

export function hasForge(w: World): boolean {
  const b = w.buildings;
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0 || b.build[i] < 1 || b.team[i] !== Team.Player) continue;
    if (FORGES.has(buildingType(b.typeId[i]).id)) return true;
  }
  return false;
}

/**
 * The wave the drop table is read at.
 *
 * `w.wave` on a settlement run, and the campaign's turn number on the world map
 * — where `w.wave` is 0 forever and a battle won in year six would otherwise
 * roll off the same table as a battle won in the first month. Floored at 1,
 * because every item in the catalogue is gated at wave 1 or later and a pool of
 * nothing is a door that quietly never opens.
 */
export function hoardWave(w: World): number {
  return Math.max(1, w.wave, w.campaign?.turn ?? 0);
}

// ── Price ──────────────────────────────────────────────────────────────────

/**
 * What it costs to take an item from `level` to `level + 1`.
 *
 * Superlinear on purpose, and this is the whole answer to "where do items go
 * late". There is no maximum level — the price is the ceiling, and it is a
 * ceiling that moves with the settlement rather than a number in a table. A
 * level-3 item is pocket change; a level-30 one is a wave's whole production,
 * which is exactly the shape a grinder wants: always affordable eventually,
 * never affordable now.
 *
 * Ingots enter at level 4, which quietly makes the second Age a prerequisite
 * for deep kit rather than an alternative to it.
 */
export function reforgeCost(def: ItemDef, level: number): Partial<ResourceBundle> {
  const l = Math.max(1, Math.floor(level));
  const r = def.rarity;
  const cost: Partial<ResourceBundle> = {
    gold: Math.round(30 * r * Math.pow(l, 1.55)),
    souls: Math.round(12 * r * Math.pow(l, 1.35)),
  };
  if (l >= 4) cost.ingots = Math.round(1.5 * r * (l - 3));
  if (l >= 8) cost.runestone = Math.round(1.2 * r * (l - 7));
  return cost;
}

/** What a thing is worth melted down. Souls only — the Hearth takes the rest. */
export function scrapValue(def: ItemDef, level: number): number {
  return Math.round(8 * def.rarity * Math.max(1, Math.floor(level)));
}

// ── Refusals ───────────────────────────────────────────────────────────────

/** '' when the item may be equipped, otherwise why not. */
export function equipError(w: World, uid: number): string {
  if (w.hero.classId === '') return 'There is nobody to carry it.';
  const st = itemByUid(w, uid);
  if (!st) return 'Not on the cart.';
  if (!itemDef(st.defId)) return 'Nobody remembers what this is.';
  if (isEquipped(w, uid)) return 'Already worn.';
  return '';
}

/** '' when the item may be reforged, otherwise why not. */
export function reforgeError(w: World, uid: number): string {
  const st = itemByUid(w, uid);
  if (!st) return 'Not on the cart.';
  const def = itemDef(st.defId);
  if (!def) return 'Nobody remembers what this is.';
  if (!hasForge(w)) return 'No fire hot enough. Build a smelter.';
  if (!canAfford(w.res, reforgeCost(def, st.level))) return 'Not enough to pay for the work.';
  return '';
}

// ── Mutation ───────────────────────────────────────────────────────────────

/**
 * Put something on the cart.
 *
 * Returns the stack, or null when the find was melted on the spot because the
 * cart was full. Refusing outright would be worse: the player would lose a rare
 * drop to a housekeeping failure they were not told about. Souls, at least, are
 * something.
 */
export function grantItem(w: World, defId: string, level: number): ItemStack | null {
  const def = itemDef(defId);
  if (!def) return null;
  const lvl = Math.max(1, Math.floor(level));

  if (w.inventory.length >= INVENTORY_MAX) {
    const souls = scrapValue(def, lvl);
    w.res.souls += souls;
    pushLog(
      w.events,
      `${def.name} goes straight on the fire — the cart is full. ${souls} Souls.`,
      LogTone.Bad,
    );
    return null;
  }

  const st: ItemStack = { uid: w.nextItemUid++, defId, level: lvl };
  w.inventory.push(st);
  pushLog(
    w.events,
    `${def.name}, ${ITEM_RARITY_NAMES[def.rarity] ?? ''} — level ${lvl}. ${def.flavour}`,
    LogTone.Lore,
  );
  return st;
}

/** Wear it. Whatever was in the slot goes back on the cart. */
export function equipItem(w: World, uid: number): boolean {
  if (equipError(w, uid) !== '') return false;
  const st = itemByUid(w, uid);
  if (!st) return false;
  const def = itemDef(st.defId);
  if (!def) return false;

  const previous = equippedStack(w, def.slot);
  w.equipped[def.slot] = uid;
  refoldEquipment(w);
  pushLog(
    w.events,
    previous
      ? `${w.hero.name} puts up ${itemDef(previous.defId)?.name ?? 'it'} and takes ${def.name}.`
      : `${w.hero.name} takes ${def.name}.`,
    LogTone.Good,
  );
  return true;
}

/** Take it off. It stays on the cart. */
export function unequipSlot(w: World, slot: number): boolean {
  if (slot < 0 || slot >= ITEM_SLOT_COUNT) return false;
  const st = equippedStack(w, slot);
  if (!st) return false;
  w.equipped[slot] = -1;
  refoldEquipment(w);
  pushLog(
    w.events,
    `${itemDef(st.defId)?.name ?? ITEM_SLOT_NAMES[slot]} goes back on the cart.`,
    LogTone.Neutral,
  );
  return true;
}

/** Feed it. One level, at a price that will be worse next time. */
export function reforgeItem(w: World, uid: number): boolean {
  if (reforgeError(w, uid) !== '') return false;
  const st = itemByUid(w, uid);
  if (!st) return false;
  const def = itemDef(st.defId);
  if (!def) return false;

  spend(w.res, reforgeCost(def, st.level));
  st.level++;
  // Only matters when it is being worn, but calling it unconditionally is free
  // and removes the branch that would eventually be got wrong.
  refoldEquipment(w);
  pushLog(w.events, `${def.name} comes off the anvil at level ${st.level}.`, LogTone.Good);
  return true;
}

/** Melt it. Taken off first if it was being worn, so the bag stays honest. */
export function scrapItem(w: World, uid: number): boolean {
  const st = itemByUid(w, uid);
  if (!st) return false;
  const def = itemDef(st.defId);

  const slot = slotOf(w, uid);
  if (slot >= 0) w.equipped[slot] = -1;
  w.inventory = w.inventory.filter((s) => s.uid !== uid);
  refoldEquipment(w);

  const souls = def ? scrapValue(def, st.level) : 0;
  w.res.souls += souls;
  pushLog(
    w.events,
    `${def?.name ?? 'It'} goes on the fire. ${souls} Souls, and nobody says anything.`,
    LogTone.Neutral,
  );
  return true;
}

// ── Where it comes from ────────────────────────────────────────────────────

/**
 * The hoard, rolled off a won field.
 *
 * Two doors, and they were picked over a third. **Surviving a Siege** is the
 * drip — half of them turn something up, which over a long run is the steady
 * trickle a grinder is built on, and it costs nothing to hook because
 * `endSiege` is already the one place that knows a wave was held. **Winning a
 * field battle** is the payout: it always drops, and it drops rarer, which is
 * the reward for the mode where the character is actually standing on the
 * ground being fought over. That is the loop the owner described — the hero is
 * on the battlefield, and the battlefield is where his kit comes from.
 *
 * The third door, the Chronicle deck, was deliberately left shut. Forty-seven
 * events each carrying their own `apply` closure is forty-seven places for the
 * drop rate to live, and an item economy whose supply is spread across a deck
 * cannot be balanced or even measured. `grantItem` is exported and the deck may
 * call it later; the *rate* should stay here.
 *
 * `bonus` shifts both the certainty and the rarity: 0 for a wave held, 1 for a
 * field won.
 */
export function rollHoard(w: World, bonus = 0): void {
  // No hero, no cart. A run that never picked a character has nowhere to put it,
  // and a hero is picked before the first horn, so this can never become "you
  // missed the drops from your first ten waves".
  if (w.hero.classId === '') return;

  const wave = hoardWave(w);
  const pool = itemsAvailable(wave);
  if (pool.length === 0) return;
  if (bonus <= 0 && nextInt(w.rngSpoils, 0, 999) >= Math.round(HOARD_CHANCE * 1000)) return;

  const weights = pool.map((d) => RARITY_WEIGHT[d.rarity] * (1 + bonus * (d.rarity - 1) * 0.6));
  const def = pickWeighted(w.rngSpoils, pool, weights);

  const level = Math.max(
    1,
    Math.floor(wave / HOARD_LEVEL_PER_WAVE) + 1 + bonus + nextInt(w.rngSpoils, 0, 1),
  );
  grantItem(w, def.id, level);
}

// ── Prose, for the sheet ───────────────────────────────────────────────────

/**
 * Human names for every modifier key, so a tooltip never shows a field name.
 *
 * Covers all of `Mods` rather than only the keys the catalogue currently uses:
 * the next item to be written should not be able to introduce a line that reads
 * `veterancyRate +12%`.
 */
export const MOD_LABELS: Record<keyof Mods, string> = {
  manaMax: 'Mana',
  unitDamage: 'Army damage',
  unitHp: 'Army health',
  moveSpeed: 'Army movement',
  attackSpeed: 'Attack speed',
  wallHp: 'Wall health',
  towerDamage: 'Tower damage',
  towerRange: 'Tower range',
  buildSpeed: 'Build speed',
  buildCost: 'Build cost',
  storage: 'Storage',
  willRegen: 'Will regeneration',
  willMax: 'Maximum Will',
  powerDamage: 'Power damage',
  powerCooldown: 'Power cooldown',
  powerRadius: 'Power radius',
  soulGain: 'Souls from kills',
  resourceRate: 'Production',
  grainUpkeep: 'Grain upkeep',
  popCap: 'Population cap',
  archerRange: 'Archer range',
  cavalryCharge: 'Cavalry charge',
  veterancyRate: 'Veterancy',
  thorns: 'Thorns',
  lifesteal: 'Lifesteal',
  siegeResist: 'Siege damage taken',
  moraleFloor: 'Morale floor',
  reinforceCost: 'Reinforcement cost',
  corpseRise: 'The fallen rise',
  willOnWaveEnd: 'Will per wave held',
  secondWind: 'Second Wind',
  hearthSave: 'Hearth spared',
};

function pct(v: number): string {
  const n = (v - 1) * 100;
  return `${n >= 0 ? '+' : '−'}${Math.abs(n).toFixed(Math.abs(n) < 10 ? 1 : 0)}%`;
}

function flat(v: number): string {
  return `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(Math.abs(v) < 10 ? 2 : 0)}`;
}

/**
 * Exactly what this item does at this level, one line per effect. Numbers, not
 * adjectives — the house rule for every tooltip in the game.
 */
export function describeItem(def: ItemDef, level: number): string[] {
  const base = defaultMods() as unknown as Record<string, number>;
  const values = itemModValues(def, level);
  const out: string[] = [];
  for (const k of Object.keys(values)) {
    const label = MOD_LABELS[k as keyof Mods] ?? k;
    out.push(`${label} ${base[k] === 0 ? flat(values[k]) : pct(values[k])}`);
  }
  if (def.heroDamage) out.push(`${'His own damage'} ${pct(1 + def.heroDamage * level)}`);
  if (def.heroWard) out.push(`Damage he takes ${pct(1 / (1 + def.heroWard * level))}`);
  return out;
}

/** Every catalogue entry, for the debug console and the codex. */
export const ITEM_CATALOGUE = ITEMS;
